import "./index.css";
import PhoneNumber from "./PhoneNumber";
import React, { useEffect, useState } from "react";
import { request } from "../../../request";
import { Col, Form, Input, message, Modal, Row } from "antd";

import type { ButtonProps } from "antd";
import type { RuleObject } from "antd/es/form";
import type { TypeLenderContactPerson } from "../../hooks/useContact";

import { ICON } from "../GuidelineSelect/GuidelineCheckbox";

const IS_REQUIRED = [{ required: true, message: `This field is required` }];

const CANCEL_BUTTON_PROPS: ButtonProps = {
  type: "link",
  style: { paddingLeft: 5 },
};

interface TypeEditContactProps
  extends Pick<TypeLenderContactPerson, "lender_id" | "lender_name"> {
  onCancel(): void;
  id: TypeLenderContactPerson["id"];
}

const EditContact: React.FC<TypeEditContactProps> = ({
  id,
  lender_id,
  lender_name,
  onCancel,
  children,
  ...user
}) => {
  const [form] = Form.useForm<TypeLenderContactPerson>();

  const [loading, setLoading] = useState(false);

  async function onSubmit() {
    try {
      setLoading(true);
      const body = await form.validateFields();
      body.id = id;
      body.lender_id = lender_id;
      await request(
        id
          ? `/api/v1/mortgagegpt/updateLenderContacts/${id}`
          : "/api/v1/mortgagegpt/createLenderContacts",
        { body, method: id ? "PUT" : "POST" },
      );
      message.success("Operation successful");
      onClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const checkPhoneNumber = (rule: RuleObject, value: string) => {
    if ([undefined, ""].includes(value)) return Promise.resolve();
    if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(value)) {
      return Promise.reject(
        new Error("Please enter a complete and valid phone number"),
      );
    }
    return Promise.resolve();
  };

  function onClose() {
    form.resetFields();
    onCancel();
  }

  useEffect(() => {
    id && form.setFieldsValue(user);
  }, [id]);

  const FIRST_NAME = Form.useWatch("first_name", form);
  const LAST_NAME = Form.useWatch("last_name", form);

  return (
    <Modal
      okText="Save"
      onOk={onSubmit}
      onCancel={onClose}
      zIndex={9999999999999}
      open={Boolean(lender_id)}
      cancelButtonProps={CANCEL_BUTTON_PROPS}
      rootClassName="edit-contact guideline-modal"
      okButtonProps={{ loading, disabled: !(FIRST_NAME && LAST_NAME) }}
      title={
        <>
          {ICON[lender_name] ? (
            <img width={25} src={ICON[lender_name]} />
          ) : null}
          {id ? "Edit" : "Add"} contact
        </>
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="first_name" label="First name" rules={IS_REQUIRED}>
              <Input allowClear size="middle" placeholder="First name" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="last_name" label="Last name" rules={IS_REQUIRED}>
              <Input allowClear placeholder="Last name" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="email"
              label="Contact Email"
              rules={[{ type: "email" }]}
            >
              <Input allowClear placeholder="Email address" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="phone_number"
              label="Work phone"
              rules={[{ validator: checkPhoneNumber }]}
            >
              <PhoneNumber allowClear placeholder="Phone number" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="role" label="Contact type">
              <Input allowClear placeholder="Enter contact type" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditContact;
